import type { TFunction } from 'i18next';
import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { i18nConfig } from './config';
import { resources } from './resources';
import type { Locale } from './types/types';

// Re-export config for non-edge usage
export {
  allowedLocales,
  allowedLocalesMMCM,
  defaultLocale,
  i18nConfig,
  setMMCMLocale,
} from './config';

const initTranslations = async (
  locale: string,
  namespaces: string[],
  options?: { keyPrefix?: string; i18nInstance?: any; resources?: any }
) => {
  const i18nInstance = options?.i18nInstance || createInstance();

  i18nInstance.use(initReactI18next);

  // Use static resources instead of dynamic imports for Turbopack compatibility
  const staticResources = options?.resources || resources;

  await i18nInstance.init({
    lng: locale,
    resources: staticResources,
    fallbackLng: i18nConfig.defaultLocale,
    supportedLngs: i18nConfig.locales,
    defaultNS: namespaces[0],
    fallbackNS: namespaces[0],
    ns: namespaces,
    debug: false,
    showSupportNotice: false,
  });

  return {
    i18n: i18nInstance,
    resources: i18nInstance.services.resourceStore.data,
    t: i18nInstance.getFixedT(
      locale,
      namespaces[0],
      options?.keyPrefix
    ) as TFunction<Locale, undefined>,
  };
};

// Exported function to fetch translations on server components
const serverSideTranslation = async (
  locale: string,
  namespaces: string[],
  options?: { keyPrefix?: string; i18nInstance?: any; resources?: any }
) => {
  return await initTranslations(locale, namespaces, options);
};

// NOTE: Using named exports for better tree-shaking
export { serverSideTranslation };
export type { AllowedLocales, I18NConfig, Locale } from './types/types';
